import { useState } from 'react';

import {
    UpdateIcon
} from '@radix-ui/react-icons';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { toast } from '../ui/use-toast';

import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import api from '../../lib/api';
import SelectBunnyPopover from '../selectBunnyPopver';
import { Checkbox } from '../ui/checkbox';
import { DropdownMenuItem } from '../ui/dropdown-menu';

function EditRegistrationDialog({ fetchClass, dropdown, registration, cup }) {

    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const form = useForm();
    const [isLoading, setIsLoading] = useState(false);

    const [formData, setFormData] = useState({
        bunnyId: registration?.bunny?.id || '',
        classId: registration?.classId || '',
        outOfCompetition: registration?.outOfCompetition || false,
        lateNotice: registration?.lateNotice || false,
        comment: registration?.comment || '',
        crossedOut: registration?.crossedOut ? true : false || false,
        inCup: registration?.in_cup || false,
        onlyCup: registration?.only_cup || false,
    });

    // Submit
    const sendEditRequest = (e) => {
        if (isLoading) return;

        e.preventDefault();
        setIsLoading(true);

        api.post(`https://skhrf-api.cmdigital.se/v1/registrations/edit`, {
            ...formData,
            registrationId: registration?.registrationId,
        })
            .then(response => {
                fetchClass();
                form.reset({
                    bunnyId: registration?.bunny?.id || '',
                    classId: registration?.classId || '',
                    outOfCompetition: registration?.outOfCompetition || false,
                    lateNotice: registration?.lateNotice || false,
                    comment: registration?.comment || '',
                    crossedOut: registration?.crossedOut ? true : false || false,
                    inCup: registration?.in_cup || false,
                    onlyCup: registration?.only_cup || false,
                });
                setIsLoading(false);
                toast({
                    title: 'Ekipaget ändrat.',
                    description: `Ekipaget har ändrats framgångsrikt.`,
                });
                setIsDialogOpen(false);
            })
            .catch(error => {
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel.',
                    description: error?.response?.data?.error,
                });
                setIsLoading(false);
            });
    };

    const handleValueChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            {dropdown ? (
                <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                    <DialogTrigger className='flex items-center w-full text-left'>
                        Redigera ekipage
                    </DialogTrigger>
                </DropdownMenuItem>
            ) : (
                <DialogTrigger className='flex items-center w-full' asChild>
                    <Button>
                        Redigera ekipage
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader className='text-left'>
                    <DialogTitle>Redigera ekipage</DialogTitle>
                    <DialogDescription>Ange information för att redigera ekipaget.</DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={sendEditRequest} className="flex flex-col gap-6">
                        <SelectBunnyPopover
                            form={form}
                            name="bunnyId"
                            label="Kanin"
                            onSelect={(bunnyId) => setFormData(prevState => ({ ...prevState, bunnyId: bunnyId }))}
                            selectedBunny={[{
                                "id": registration?.bunny?.id,
                                "full_name": registration?.bunny?.name,
                                "registration_number": registration?.bunny?.registrationNumber,
                            }]}
                        />
                        <FormField
                            control={form.control}
                            name="comment"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Anmälningskommentar (frivillig, max 70 tecken)</FormLabel>
                                    <FormControl>
                                        <Input placeholder="" name="comment" type='text' value={formData.comment} onChange={handleValueChange} disabled={isLoading} autoComplete='off' maxLength="70" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="outOfCompetition"
                            render={({ field }) => (
                                <FormItem className='space-y-0 flex items-center gap-2'>
                                    <FormControl>
                                        <Checkbox checked={formData.outOfCompetition}
                                            onCheckedChange={(checked) => {
                                                const updatedValue = checked === true;
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    outOfCompetition: updatedValue,
                                                }));
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel>Markera ekipaget som utom tävlan</FormLabel>
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="lateNotice"
                            render={({ field }) => (
                                <FormItem className='space-y-0 flex items-center gap-2'>
                                    <FormControl>
                                        <Checkbox checked={formData.lateNotice} onCheckedChange={(checked) => {
                                            const updatedValue = checked === true;
                                            setFormData((prev) => ({
                                                ...prev,
                                                lateNotice: updatedValue,
                                            }));
                                            field.onChange(updatedValue);
                                        }}
                                        />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel>Markera ekipaget som sent anmält</FormLabel>
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="crossedOut"
                            render={({ field }) => (
                                <FormItem className='space-y-0 flex items-center gap-2'>
                                    <FormControl>
                                        <Checkbox checked={formData.crossedOut} onCheckedChange={(checked) => {
                                            const updatedValue = checked === true;
                                            setFormData((prev) => ({
                                                ...prev,
                                                crossedOut: updatedValue,
                                            }));
                                            field.onChange(updatedValue);
                                        }}
                                        />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel>Markera ekipage som struket</FormLabel>
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="onlyCup"
                            render={({ field }) => (
                                <FormItem className='space-y-0 flex items-center gap-2'>
                                    <FormControl>
                                        <Checkbox checked={formData.onlyCup} onCheckedChange={(checked) => {
                                            const updatedValue = checked === true;
                                            setFormData((prev) => ({
                                                ...prev,
                                                onlyCup: updatedValue,
                                            }));
                                            field.onChange(updatedValue);
                                        }}
                                        />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel>Markera ekipage som endast cup</FormLabel>
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {(cup?.id || formData.inCup === true) &&
                            <FormField
                                control={form.control}
                                name="inCup"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <div className='flex gap-2'>
                                            <FormControl>
                                                <Checkbox checked={formData.inCup} onCheckedChange={(checked) => {
                                                    const updatedValue = checked === true;
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        inCup: updatedValue,
                                                    }));
                                                    field.onChange(updatedValue);
                                                }}
                                                />
                                            </FormControl>
                                            <div className="space-y-1 leading-none">
                                                <FormLabel>Inkludera ekipage i cup</FormLabel>
                                            </div>
                                        </div>
                                        <FormMessage />
                                        <FormDescription className='text-muted-foreground text-sm'>
                                            Du håller på att lägga till ett ekipage i en klass som tillhör cupen
                                            {cup?.id ?
                                                <Link to={`/competition/${registration?.classId}/cup/${cup?.id}`}
                                                    className="text-blue-600 font-medium hover:underline mr-2" target="_blank" >
                                                    {' ' + cup?.name}.
                                                </Link>
                                                : '. '
                                            }
                                            Tänk på att ekipaget måste vara anmält och slutföra minst 50 % av tillhörande klasserna för att ett cupresultat för kaninen ska skapas.
                                        </FormDescription>
                                    </FormItem>
                                )}
                            />
                        }
                        <div className='grid grid-cols-2 gap-3'>
                            <DialogClose asChild>
                                <Button type='button' variant='secondary'>Avbryt</Button>
                            </DialogClose>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Redigera ekipage</>}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default EditRegistrationDialog;
